import { useState } from "react";
import { LoaderCircle, Plus, Trash2 } from "lucide-react";
import { useRouter } from "@shared/client/navigation";
import type { AdminUserRecord } from "@shared/lib/admin-users";
import type { ClientRole } from "@shared/lib/admin-db";
import { MIN_PASSWORD_LENGTH } from "@shared/lib/auth-shared";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@shared/components/ui/dialog";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { Switch } from "@shared/components/ui/switch";

export const ROLE_LABELS: Record<ClientRole, string> = {
  owner: "Propriétaire",
  admin: "Admin",
  member: "Membre",
  viewer: "Lecture seule",
};

type Access = { clientId: string; role: ClientRole };

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-b py-5 first:pt-1 last:border-b-0">
      <div className="mb-3.5 flex items-center justify-between gap-4">
        <h3 className="text-[14px] font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-[13px] font-normal text-muted-foreground" htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

/**
 * Création (user = null) ou modification d'un compte : profil, mot de passe,
 * droit d'admin global et accès aux clients, enregistrés en un envoi.
 */
export function UserFormDialog({
  open,
  user,
  clients,
  currentUserId,
  onClose,
}: {
  open: boolean;
  user: AdminUserRecord | null;
  clients: Array<{ id: string; name: string }>;
  currentUserId: string | null;
  onClose: () => void;
}) {
  return (
    <Dialog onOpenChange={(next) => !next && onClose()} open={open}>
      <DialogContent className="flex max-h-[min(820px,calc(100dvh-2rem))] flex-col gap-0 p-0 sm:max-w-2xl">
        {open && (
          <UserForm clients={clients} currentUserId={currentUserId} key={user?.id ?? "new"} onClose={onClose} user={user} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserForm({
  user,
  clients,
  currentUserId,
  onClose,
}: {
  user: AdminUserRecord | null;
  clients: Array<{ id: string; name: string }>;
  currentUserId: string | null;
  onClose: () => void;
}) {
  const router = useRouter();
  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [lastName, setLastName] = useState(user?.last_name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isAdmin, setIsAdmin] = useState(user?.is_admin ?? false);
  const [access, setAccess] = useState<Access[]>(
    () => user?.access.map((item) => ({ clientId: item.client_id, role: item.role })) ?? [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isSelf = user !== null && user.id === currentUserId;
  const clientName = new Map(clients.map((client) => [client.id, client.name]));
  const available = clients.filter((client) => !access.some((item) => item.clientId === client.id));

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    if (password && password.length < MIN_PASSWORD_LENGTH) {
      setError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`);
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(user ? `/api/admin/users/${user.id}` : "/api/admin/users", {
        method: user ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName, lastName, email, password: password || undefined, isAdmin, access }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Enregistrement impossible.");
      router.refresh();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
      <DialogHeader className="border-b px-6 pt-6 pb-4 text-left">
        <DialogTitle className="text-[17px] font-semibold">{user ? "Modifier l’utilisateur" : "Ajouter un utilisateur"}</DialogTitle>
        <DialogDescription>
          {user
            ? `Inscrit ${user.has_google ? "avec Google" : "par e-mail"}${user.has_password ? "" : ", sans mot de passe"}.`
            : "Sans mot de passe, la personne reçoit par e-mail un lien pour choisir le sien."}
        </DialogDescription>
      </DialogHeader>

      <ScrollArea className="min-h-0 flex-1">
        <div className="px-6 py-4">
          <Section title="Informations de base">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="user-first-name" label="Prénom">
                <Input autoComplete="off" id="user-first-name" onChange={(e) => setFirstName(e.target.value)} required value={firstName} />
              </Field>
              <Field id="user-last-name" label="Nom">
                <Input autoComplete="off" id="user-last-name" onChange={(e) => setLastName(e.target.value)} required value={lastName} />
              </Field>
              <Field id="user-email" label="E-mail">
                <Input autoComplete="off" id="user-email" onChange={(e) => setEmail(e.target.value)} required type="email" value={email} />
              </Field>
            </div>
          </Section>

          <Section title={user ? "Changer le mot de passe" : "Mot de passe"}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="user-password" label={user ? "Nouveau mot de passe (vide : inchangé)" : "Mot de passe (vide : lien d’invitation)"}>
                <Input autoComplete="new-password" id="user-password" onChange={(e) => setPassword(e.target.value)} type="password" value={password} />
              </Field>
              <Field id="user-password-confirm" label="Confirmer le mot de passe">
                <Input
                  autoComplete="new-password"
                  disabled={!password}
                  id="user-password-confirm"
                  onChange={(e) => setConfirm(e.target.value)}
                  type="password"
                  value={confirm}
                />
              </Field>
            </div>
            {user && !isSelf && (
              <p className="mt-2.5 mb-0 text-[12.5px] text-muted-foreground">Un nouveau mot de passe déconnecte ses sessions ouvertes.</p>
            )}
          </Section>

          <Section
            action={
              <label className="flex items-center gap-2.5 text-[13.5px]" htmlFor="user-admin">
                Administrateur global
                <Switch checked={isAdmin} disabled={isSelf && isAdmin} id="user-admin" onCheckedChange={setIsAdmin} />
              </label>
            }
            title="Droits d’accès"
          >
            {isSelf && (
              <p className="mt-0 mb-3 text-[12.5px] text-muted-foreground">Vous ne pouvez pas retirer votre propre droit d’administration.</p>
            )}
            <div className="rounded-lg border">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
                <span className="text-[13.5px] font-medium">Accès aux clients</span>
                <Select
                  disabled={available.length === 0}
                  onValueChange={(clientId) => setAccess((list) => [...list, { clientId, role: "member" }])}
                  value=""
                >
                  <SelectTrigger aria-label="Ajouter un client" className="h-8 min-w-[200px] text-[13px]" size="sm">
                    <Plus className="size-3.5 text-muted-foreground" />
                    <SelectValue placeholder={available.length ? "Ajouter un client" : "Aucun autre client"} />
                  </SelectTrigger>
                  <SelectContent>
                    {available.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {access.length === 0 ? (
                <p className="m-0 px-4 py-6 text-center text-[13px] text-muted-foreground">
                  Rattaché à aucun client. Le compte garde son propre espace.
                </p>
              ) : (
                <ul className="m-0 list-none p-0">
                  {access.map((item) => (
                    <li className="flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0" key={item.clientId}>
                      <span className="min-w-0 flex-1 truncate text-[13.5px]">{clientName.get(item.clientId) ?? "Client supprimé"}</span>
                      <Select
                        onValueChange={(role) =>
                          setAccess((list) => list.map((entry) => (entry.clientId === item.clientId ? { ...entry, role: role as ClientRole } : entry)))
                        }
                        value={item.role}
                      >
                        <SelectTrigger aria-label={`Rôle pour ${clientName.get(item.clientId) ?? "ce client"}`} className="h-8 w-[150px] text-[13px]" size="sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(ROLE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        aria-label={`Retirer l’accès à ${clientName.get(item.clientId) ?? "ce client"}`}
                        onClick={() => setAccess((list) => list.filter((entry) => entry.clientId !== item.clientId))}
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <Trash2 className="text-muted-foreground" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Section>

          {error && (
            <Alert className="mt-1 mb-2" role="alert" variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </ScrollArea>

      <DialogFooter className="border-t px-6 py-3.5">
        <Button onClick={onClose} type="button" variant="outline">Annuler</Button>
        <Button disabled={busy} type="submit">
          {busy && <LoaderCircle className="animate-spin" />}
          {user ? "Mettre à jour" : "Créer l’utilisateur"}
        </Button>
      </DialogFooter>
    </form>
  );
}
